import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

/**
 * Custom hook for Parts Catalog data operations
 * Handles all data fetching, CRUD operations, and related state management
 */
export const usePartsCatalog = () => {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 50,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [dropdownOptions, setDropdownOptions] = useState({
    categories: [],
    manufacturerNames: [],
    assemblyTypes: [],
    ataChapters: [],
    primaryModels: [],
    modelVariants: []
  });
  const fetchGenerationRef = useRef(0);

  const fetchDropdownOptions = useCallback(async () => {
    try {
      const [categoriesRes, manufacturersRes, assemblyTypesRes, ataChaptersRes, allModelsRes] = await Promise.all([
        axios.get('/api/master-data/part-categories', { params: { isActive: 'true' } }),
        axios.get('/api/master-data/manufacturer-names', { params: { isActive: 'true' } }),
        axios.get('/api/master-data/assembly-types', { params: { isActive: 'true' } }),
        axios.get('/api/master-data/ata-chapters', { params: { isActive: 'true' } }),
        axios.get('/api/master-data/models', { params: { isActive: 'true', includePrimaryModels: 'true' } })
      ]);

      // Filter primary models: models that have type field set AND no primaryModel field
      // Primary models are models with type ('Fixed Wing' or 'Rotary Wing') and no primaryModel reference
      const primaryModels = allModelsRes.data.filter(model => 
        model.type && !model.primaryModel
      );

      // Filter model variants: models that have primaryModel field OR no type field
      const modelVariants = allModelsRes.data.filter(model => 
        model.primaryModel || !model.type
      );

      setDropdownOptions({
        categories: categoriesRes.data,
        manufacturerNames: manufacturersRes.data,
        assemblyTypes: assemblyTypesRes.data,
        ataChapters: ataChaptersRes.data,
        primaryModels: primaryModels,
        modelVariants: modelVariants
      });
    } catch (error) {
      console.error('Error fetching dropdown options:', error);
    }
  }, []);

  const fetchParts = useCallback(async (filters, showArchived, page = 1, pageSize = 50, searchTerm = '', signal = null) => {
    const generation = ++fetchGenerationRef.current;
    setLoading(true);
    try {
      const params = {
        ...(filters.category && { category: filters.category }),
        ...(filters.manufacturer && { manufacturer: filters.manufacturer }),
        ...(searchTerm && { search: searchTerm }),
        page,
        pageSize
      };
      // When showArchived is false: show only active parts
      // When showArchived is true: show only archived parts
      params.isActive = showArchived ? 'false' : 'true';
      const config = signal ? { params, signal } : { params };
      const response = await axios.get('/api/parts', config);

      // Ignore stale / aborted responses so a newer fetch owns loading + data
      if (signal?.aborted || generation !== fetchGenerationRef.current) return;

      if (response.data.parts) {
        setParts(response.data.parts);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      } else {
        // Fallback for old API format
        setParts(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      if ((axios.isCancel && axios.isCancel(error)) || error?.code === 'ERR_CANCELED') return;
      if (generation !== fetchGenerationRef.current) return;
      console.error('Error fetching parts:', error);
      setParts([]);
    } finally {
      // Only the latest in-flight request may clear loading (avoids stuck spinner after abort)
      if (generation === fetchGenerationRef.current) setLoading(false);
    }
  }, []);

  const handleDelete = async (partId, partNumber) => {
    if (!window.confirm('Are you sure you want to delete this part? This action cannot be undone.')) {
      return { success: false };
    }
    
    try {
      await axios.delete(`/api/parts/${partId}`);
      return { success: true };
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.dependencies) {
        // Part has dependencies - return dependency data
        return { 
          success: false, 
          hasDependencies: true, 
          dependencyData: error.response.data,
          partToDelete: { id: partId, partNumber }
        };
      } else {
        alert(error.response?.data?.message || 'Error deleting part');
        return { success: false };
      }
    }
  };

  const handleForceDelete = async (partId) => {
    if (!window.confirm('Are you absolutely sure you want to delete this part? This action cannot be undone.')) {
      return false;
    }
    
    try {
      await axios.delete(`/api/parts/${partId}`);
      return true;
    } catch (error) {
      alert(error.response?.data?.message || 'Error deleting part');
      return false;
    }
  };

  const handleSubmit = async (formData, selectedPart, isQuickAdd = false) => {
    try {
      // Process serial numbers: always send an array so backend can persist "no serials"
      let serialNumbers = [];
      if (formData.serialNumberRequired && formData.serialNumbers) {
        serialNumbers = formData.serialNumbers.map(sn => sn.trim()).filter(sn => sn.length > 0);
      }
      
      // Send arrays for multi-select fields
      const submitData = {
        ...formData,
        category: formData.category || undefined,
        manufacturers: Array.isArray(formData.manufacturerNames) && formData.manufacturerNames.length > 0
          ? formData.manufacturerNames
          : undefined,
        primaryModels: Array.isArray(formData.primaryModels) && formData.primaryModels.length > 0
          ? formData.primaryModels
          : undefined,
        modelVariants: Array.isArray(formData.modelVariants) && formData.modelVariants.length > 0
          ? formData.modelVariants
          : undefined,
        ataChapter: formData.ataChapter || undefined,
        applicableAssemblyTypes: formData.applicableAssemblyTypes || [],
        serialNumbers,
        serialNumberRequired: formData.serialNumberRequired || false
      };
      
      // Remove frontend-only field names
      delete submitData.manufacturerNames;

      if (selectedPart) {
        await axios.put(`/api/parts/${selectedPart._id}`, submitData);
      } else {
        await axios.post('/api/parts', submitData);
      }
      
      return { success: true };
    } catch (error) {
      const data = error.response?.data;
      let message = data?.message;
      if (!message && Array.isArray(data?.errors) && data.errors.length > 0) {
        message = data.errors.map((e) => {
          const field = e.path || e.param || 'Field';
          const label = field.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();
          return `${label}: ${e.msg || e.message || 'Invalid value'}`;
        }).join('. ');
      }
      alert(message || 'Error saving part');
      return { success: false };
    }
  };

  // Listen for master data updates to refresh dropdowns
  useEffect(() => {
    const handleMasterDataUpdate = (event) => {
      // Refresh dropdown options when master data is updated
      fetchDropdownOptions();
    };

    window.addEventListener('masterDataUpdated', handleMasterDataUpdate);

    return () => {
      window.removeEventListener('masterDataUpdated', handleMasterDataUpdate);
    };
  }, [fetchDropdownOptions]);

  return {
    parts,
    setParts,
    loading,
    pagination,
    setPagination,
    dropdownOptions,
    fetchDropdownOptions,
    fetchParts,
    handleDelete,
    handleForceDelete,
    handleSubmit
  };
};
